import React, { useState, useEffect, useCallback } from 'react';

interface ToastFlipProps {
  width?: number;
  height?: number;
  className?: string;
}

/**
 * Animated toaster illustration — 14-frame sprite animation.
 * 500ms between frames, 2s pause before looping from frame 14 → 1.
 */
const ToastFlip: React.FC<ToastFlipProps> = ({
  width = 108,
  height = 108,
  className,
}) => {
  const [frame, setFrame] = useState(0);

  const advance = useCallback(() => {
    setFrame((prev) => (prev + 1) % 14);
  }, []);

  useEffect(() => {
    const delay = frame === 13 ? 2000 : 500;
    const timer = setTimeout(advance, delay);
    return () => clearTimeout(timer);
  }, [frame, advance]);

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 108 108"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {frames[frame]}
    </svg>
  );
};

/* ---------- shared bottom half (toaster body, base, handles) ---------- */
const toasterBody = (
  <>
    <path d="M20.5435 76.2717C20.5435 69.6443 25.916 64.2717 32.5435 64.2717H74.8695C81.497 64.2717 86.8695 69.6443 86.8695 76.2717V104.772H20.5435V76.2717Z" fill="#D5F9EE" stroke="#1B8E56" strokeWidth="2"/>
    <path d="M20.5435 73.076H22.7608C26.0746 73.076 28.7608 75.7623 28.7608 79.076V89.9674C28.7608 93.2811 26.0746 95.9674 22.7608 95.9674H20.5435V73.076Z" fill="#7DDDB5" stroke="#1B8E56" strokeWidth="2"/>
    <path d="M66.326 73.3152C66.326 72.2106 67.2215 71.3152 68.326 71.3152H69.0217C70.1263 71.3152 71.0217 72.2106 71.0217 73.3152V95.7282C71.0217 96.8328 70.1263 97.7282 69.0217 97.7282H68.3261C67.2215 97.7282 66.326 96.8328 66.326 95.7282V73.3152Z" fill="#7DDDB5" stroke="#1B8E56" strokeWidth="2"/>
    <path d="M86.8695 73.076H84.6521C81.3384 73.076 78.6521 75.7623 78.6521 79.076V89.9674C78.6521 93.2811 81.3384 95.9674 84.6521 95.9674H86.8695V73.076Z" fill="#7DDDB5" stroke="#1B8E56" strokeWidth="2"/>
    <path d="M33.4565 94.7935H38.1522" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <path d="M42.8478 94.7935H47.5434" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <path d="M15.2609 104.772H92.1522" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
  </>
);

/* lever knob — two positions: high (93) and low (80) */
const leverHigh = (
  <rect x="63.9783" y="93.0326" width="4.69565" height="9.3913" rx="2.34783" transform="rotate(-90 63.9783 93.0326)" fill="white" stroke="#1B8E56" strokeWidth="2"/>
);
const leverLow = (
  <rect x="63.9783" y="80.7065" width="4.69565" height="9.3913" rx="2.34783" transform="rotate(-90 63.9783 80.7065)" fill="white" stroke="#1B8E56" strokeWidth="2"/>
);

/* toast inside slot — upright at y=67 */
const toastInSlotHigh = (
  <>
    <rect x="39.326" y="67.2065" width="29.3478" height="35.2174" rx="4" fill="white" stroke="#1B8E56" strokeWidth="2"/>
    <rect width="35.8043" height="9.3913" rx="4.69565" transform="matrix(1 0 0 -1 35.8043 102.424)" fill="white"/>
    <path d="M41.0869 93.0326H40.5C37.9066 93.0326 35.8043 95.135 35.8043 97.7283C35.8043 100.322 37.9066 102.424 40.5 102.424H66.913C69.5064 102.424 71.6087 100.322 71.6087 97.7283C71.6087 95.135 69.5064 93.0326 66.913 93.0326" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <rect x="35.8043" y="67.2065" width="35.8043" height="9.3913" rx="4.69565" fill="white"/>
    <path d="M41.0869 76.5978H40.5C37.9066 76.5978 35.8043 74.4955 35.8043 71.9022C35.8043 69.3089 37.9066 67.2065 40.5 67.2065H66.913C69.5064 67.2065 71.6087 69.3089 71.6087 71.9022C71.6087 74.4955 69.5064 76.5978 66.913 76.5978" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="46.9565" cy="73.6631" r="1.76087" fill="#1B8E56"/>
    <circle cx="51.6521" cy="77.1848" r="1.76087" fill="#1B8E56"/>
    <circle cx="58.1086" cy="76.0109" r="1.76087" fill="#1B8E56"/>
    <circle cx="61.0435" cy="81.2935" r="1.76087" fill="#1B8E56"/>
    <circle cx="45.7826" cy="83.0543" r="1.76087" fill="#1B8E56"/>
    <circle cx="55.7609" cy="89.5109" r="1.76087" fill="#1B8E56"/>
    <circle cx="61.6304" cy="97.1414" r="1.76087" fill="#1B8E56"/>
    <circle cx="47.5435" cy="95.9674" r="1.76087" fill="#1B8E56"/>
  </>
);

/* toast popping out — y=43 */
const toastPopHalf = (
  <>
    <rect x="39.326" y="43.7283" width="29.3478" height="35.2174" rx="4" fill="white" stroke="#1B8E56" strokeWidth="2"/>
    <rect width="35.8043" height="9.3913" rx="4.69565" transform="matrix(1 0 0 -1 35.8043 78.9457)" fill="white"/>
    <path d="M41.0869 69.5544H40.5C37.9066 69.5544 35.8043 71.6567 35.8043 74.25C35.8043 76.8434 37.9066 78.9457 40.5 78.9457H66.913C69.5064 78.9457 71.6087 76.8434 71.6087 74.25C71.6087 71.6567 69.5064 69.5544 66.913 69.5544" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <rect x="35.8043" y="43.7283" width="35.8043" height="9.3913" rx="4.69565" fill="white"/>
    <path d="M41.0869 53.1196H40.5C37.9066 53.1196 35.8043 51.0173 35.8043 48.4239C35.8043 45.8306 37.9066 43.7283 40.5 43.7283H66.913C69.5064 43.7283 71.6087 45.8306 71.6087 48.4239C71.6087 51.0173 69.5064 53.1196 66.913 53.1196" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="46.9565" cy="50.1848" r="1.76087" fill="#1B8E56"/>
    <circle cx="51.6521" cy="53.7065" r="1.76087" fill="#1B8E56"/>
    <circle cx="58.1086" cy="52.5326" r="1.76087" fill="#1B8E56"/>
    <circle cx="61.0435" cy="57.8152" r="1.76087" fill="#1B8E56"/>
    <circle cx="45.7826" cy="59.5761" r="1.76087" fill="#1B8E56"/>
    <circle cx="55.7609" cy="66.0326" r="1.76087" fill="#1B8E56"/>
    <circle cx="61.6304" cy="73.6631" r="1.76087" fill="#1B8E56"/>
    <circle cx="47.5435" cy="72.4891" r="1.76087" fill="#1B8E56"/>
  </>
);

/* toast mid-air — y=26 */
const toastMidAir = (
  <>
    <rect x="39.326" y="26.1201" width="29.3478" height="35.2174" rx="4" fill="white" stroke="#1B8E56" strokeWidth="2"/>
    <rect width="35.8043" height="9.3913" rx="4.69565" transform="matrix(1 0 0 -1 35.8043 61.3375)" fill="white"/>
    <path d="M41.0869 51.9462H40.5C37.9066 51.9462 35.8043 54.0485 35.8043 56.6419C35.8043 59.2352 37.9066 61.3375 40.5 61.3375H66.913C69.5064 61.3375 71.6087 59.2352 71.6087 56.6419C71.6087 54.0485 69.5064 51.9462 66.913 51.9462" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <rect x="35.8043" y="26.1201" width="35.8043" height="9.3913" rx="4.69565" fill="white"/>
    <path d="M41.0869 35.5114H40.5C37.9066 35.5114 35.8043 33.4091 35.8043 30.8158C35.8043 28.2224 37.9066 26.1201 40.5 26.1201L66.913 26.1201C69.5064 26.1201 71.6087 28.2224 71.6087 30.8158C71.6087 33.4091 69.5064 35.5114 66.913 35.5114" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="46.9565" cy="32.5767" r="1.76087" fill="#1B8E56"/>
    <circle cx="51.6521" cy="36.0984" r="1.76087" fill="#1B8E56"/>
    <circle cx="58.1086" cy="34.9244" r="1.76087" fill="#1B8E56"/>
    <circle cx="61.0435" cy="40.207" r="1.76087" fill="#1B8E56"/>
    <circle cx="45.7826" cy="41.9679" r="1.76087" fill="#1B8E56"/>
    <circle cx="55.7609" cy="48.4244" r="1.76087" fill="#1B8E56"/>
    <circle cx="61.6304" cy="56.0549" r="1.76087" fill="#1B8E56"/>
    <circle cx="47.5435" cy="54.881" r="1.76087" fill="#1B8E56"/>
  </>
);

/* toast at top — y=8 */
const toastTop = (
  <>
    <rect x="39.326" y="8.51147" width="29.3478" height="35.2174" rx="4" fill="white" stroke="#1B8E56" strokeWidth="2"/>
    <rect width="35.8043" height="9.3913" rx="4.69565" transform="matrix(1 0 0 -1 35.8043 43.7289)" fill="white"/>
    <path d="M41.0869 34.3376H40.5C37.9066 34.3376 35.8043 36.4399 35.8043 39.0332C35.8043 41.6266 37.9066 43.7289 40.5 43.7289H66.913C69.5064 43.7289 71.6087 41.6266 71.6087 39.0332C71.6087 36.4399 69.5064 34.3376 66.913 34.3376" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <rect x="35.8043" y="8.51147" width="35.8043" height="9.3913" rx="4.69565" fill="white"/>
    <path d="M41.0869 17.9028H40.5C37.9066 17.9028 35.8043 15.8005 35.8043 13.2071C35.8043 10.6138 37.9066 8.51147 40.5 8.51147L66.913 8.51147C69.5064 8.51147 71.6087 10.6138 71.6087 13.2071C71.6087 15.8005 69.5064 17.9028 66.913 17.9028" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="46.9565" cy="14.968" r="1.76087" fill="#1B8E56"/>
    <circle cx="51.6521" cy="18.4898" r="1.76087" fill="#1B8E56"/>
    <circle cx="58.1086" cy="17.3158" r="1.76087" fill="#1B8E56"/>
    <circle cx="61.0435" cy="22.5984" r="1.76087" fill="#1B8E56"/>
    <circle cx="45.7826" cy="24.3593" r="1.76087" fill="#1B8E56"/>
    <circle cx="55.7609" cy="30.8158" r="1.76087" fill="#1B8E56"/>
    <circle cx="61.6304" cy="38.4463" r="1.76087" fill="#1B8E56"/>
    <circle cx="47.5435" cy="37.2723" r="1.76087" fill="#1B8E56"/>
  </>
);

/* toast rotated -45° (tilted left) */
const toastRotNeg45 = (
  <>
    <rect x="31.0868" y="41.4454" width="29.3478" height="35.2174" rx="4" transform="rotate(-45 31.0868 41.4454)" fill="white" stroke="#1B8E56" strokeWidth="2"/>
    <rect width="35.8043" height="9.3913" rx="4.69565" transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 53.499 68.8381)" fill="white"/>
    <path d="M50.5937 58.4621L50.1787 58.8772C48.3449 60.7109 48.3449 63.684 50.1787 65.5178C52.0125 67.3516 54.9856 67.3516 56.8193 65.5178L75.4962 46.841C77.33 45.0072 77.33 42.0341 75.4962 40.2003C73.6624 38.3665 70.6893 38.3665 68.8555 40.2003" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <rect x="28.5966" y="43.9357" width="35.8043" height="9.3913" rx="4.69565" transform="rotate(-45 28.5966 43.9357)" fill="white"/>
    <path d="M38.9726 46.841L38.5575 47.256C36.7238 49.0898 33.7507 49.0898 31.9169 47.256C30.0831 45.4222 30.0831 42.4491 31.9169 40.6153L50.5937 21.9385C52.4275 20.1047 55.4006 20.1047 57.2344 21.9385C59.0681 23.7723 59.0681 26.7454 57.2344 28.5792" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="41.0477" cy="40.6154" r="1.76087" transform="rotate(-45 41.0477 40.6154)" fill="#1B8E56"/>
    <circle cx="46.8584" cy="39.7853" r="1.76087" transform="rotate(-45 46.8584 39.7853)" fill="#1B8E56"/>
    <circle cx="50.5938" cy="34.3898" r="1.76087" transform="rotate(-45 50.5938 34.3898)" fill="#1B8E56"/>
    <circle cx="56.4043" cy="36.0499" r="1.76087" transform="rotate(-45 56.4043 36.0499)" fill="#1B8E56"/>
    <circle cx="46.8584" cy="48.0861" r="1.76087" transform="rotate(-45 46.8584 48.0861)" fill="#1B8E56"/>
    <circle cx="58.4795" cy="45.5958" r="1.76087" transform="rotate(-45 58.4795 45.5958)" fill="#1B8E56"/>
    <circle cx="68.0254" cy="46.8409" r="1.76087" transform="rotate(-45 68.0254 46.8409)" fill="#1B8E56"/>
    <circle cx="57.2344" cy="55.9718" r="1.76087" transform="rotate(-45 57.2344 55.9718)" fill="#1B8E56"/>
  </>
);

/* toast rotated -90° (sideways) */
const toastRot90 = (
  <>
    <rect x="36.0979" y="40.5006" width="29.3478" height="35.2174" rx="4" transform="rotate(-90 36.0979 40.5006)" fill="white" stroke="#1B8E56" strokeWidth="2"/>
    <rect width="35.8043" height="9.3913" rx="4.69565" transform="matrix(-4.37114e-08 -1 -1 4.37114e-08 71.3153 44.0223)" fill="white"/>
    <path d="M61.924 38.7397L61.924 39.3267C61.924 41.92 64.0263 44.0223 66.6197 44.0223C69.213 44.0223 71.3153 41.92 71.3153 39.3267L71.3153 12.9136C71.3153 10.3203 69.213 8.21799 66.6197 8.21799C64.0263 8.21799 61.924 10.3203 61.924 12.9136" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <rect x="36.0979" y="44.0223" width="35.8043" height="9.3913" rx="4.69565" transform="rotate(-90 36.0979 44.0223)" fill="white"/>
    <path d="M45.4892 38.7397L45.4892 39.3267C45.4892 41.92 43.3869 44.0223 40.7936 44.0223C38.2002 44.0223 36.0979 41.92 36.0979 39.3267L36.0979 12.9136C36.0979 10.3203 38.2002 8.21799 40.7936 8.21799C43.3869 8.21799 45.4892 10.3203 45.4892 12.9136" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="42.5544" cy="32.8701" r="1.76087" transform="rotate(-90 42.5544 32.8701)" fill="#1B8E56"/>
    <circle cx="46.0762" cy="28.1746" r="1.76087" transform="rotate(-90 46.0762 28.1746)" fill="#1B8E56"/>
    <circle cx="44.9022" cy="21.718" r="1.76087" transform="rotate(-90 44.9022 21.718)" fill="#1B8E56"/>
    <circle cx="50.1848" cy="18.7832" r="1.76087" transform="rotate(-90 50.1848 18.7832)" fill="#1B8E56"/>
    <circle cx="51.9457" cy="34.0441" r="1.76087" transform="rotate(-90 51.9457 34.0441)" fill="#1B8E56"/>
    <circle cx="58.4022" cy="24.0658" r="1.76087" transform="rotate(-90 58.4022 24.0658)" fill="#1B8E56"/>
    <circle cx="66.0327" cy="18.1963" r="1.76087" transform="rotate(-90 66.0327 18.1963)" fill="#1B8E56"/>
    <circle cx="64.8588" cy="32.2832" r="1.76087" transform="rotate(-90 64.8588 32.2832)" fill="#1B8E56"/>
  </>
);

/* toast rotated -135° (upside-down tilted) */
const toastRot135 = (
  <>
    <rect x="51.4238" y="48.7397" width="29.3478" height="35.2174" rx="4" transform="rotate(-135 51.4238 48.7397)" fill="white" stroke="#1B8E56" strokeWidth="2"/>
    <rect width="35.8043" height="9.3913" rx="4.69565" transform="matrix(-0.707107 -0.707107 -0.707107 0.707107 78.8165 26.3275)" fill="white"/>
    <path d="M68.4405 29.2328L68.8555 29.6478C70.6893 31.4816 73.6624 31.4816 75.4962 29.6478C77.33 27.8141 77.33 24.841 75.4962 23.0072L56.8194 4.33035C54.9856 2.49658 52.0125 2.49658 50.1787 4.33035C48.3449 6.16411 48.3449 9.13724 50.1787 10.971" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <rect x="53.9141" y="51.23" width="35.8043" height="9.3913" rx="4.69565" transform="rotate(-135 53.9141 51.23)" fill="white"/>
    <path d="M56.8193 40.854L57.2344 41.269C59.0682 43.1028 59.0682 46.0759 57.2344 47.9097C55.4006 49.7434 52.4275 49.7434 50.5937 47.9097L31.9169 29.2328C30.0831 27.399 30.0831 24.4259 31.9169 22.5922C33.7507 20.7584 36.7238 20.7584 38.5575 22.5922" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="50.5938" cy="38.7788" r="1.76087" transform="rotate(-135 50.5938 38.7788)" fill="#1B8E56"/>
    <circle cx="49.7637" cy="32.9681" r="1.76087" transform="rotate(-135 49.7637 32.9681)" fill="#1B8E56"/>
    <circle cx="44.3682" cy="29.2328" r="1.76087" transform="rotate(-135 44.3682 29.2328)" fill="#1B8E56"/>
    <circle cx="46.0283" cy="23.4222" r="1.76087" transform="rotate(-135 46.0283 23.4222)" fill="#1B8E56"/>
    <circle cx="58.0645" cy="32.9681" r="1.76087" transform="rotate(-135 58.0645 32.9681)" fill="#1B8E56"/>
    <circle cx="55.5742" cy="21.347" r="1.76087" transform="rotate(-135 55.5742 21.347)" fill="#1B8E56"/>
    <circle cx="56.8193" cy="11.8011" r="1.76087" transform="rotate(-135 56.8193 11.8011)" fill="#1B8E56"/>
    <circle cx="65.9502" cy="22.5922" r="1.76087" transform="rotate(-135 65.9502 22.5922)" fill="#1B8E56"/>
  </>
);

/* particles (appear in flying frames) */
const particles = (
  <>
    <circle cx="85.1087" cy="35.511" r="2.93478" stroke="#1B8E56" strokeWidth="2"/>
    <circle cx="17.0217" cy="50.1848" r="1.76087" fill="#1B8E56"/>
  </>
);

/* steam lines */
const steam1 = (
  <path d="M47.5435 42.8478C46.6631 43.4347 44.9022 45.0782 44.9022 46.9565C44.9022 48.8347 46.2718 49.6956 46.9566 49.8913C48.3261 50.3804 50.0087 52.4152 45.7827 56.6413" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
);
const steam2 = (
  <path d="M54.2935 42.8478C53.4131 43.4347 51.6522 45.0782 51.6522 46.9565C51.6522 48.8347 53.0218 49.6956 53.7066 49.8913C55.0761 50.3804 56.7587 52.4152 52.5327 56.6413" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
);
const steam3 = (
  <path d="M61.337 42.8478C60.4565 43.4347 58.6957 45.0782 58.6957 46.9565C58.6957 48.8347 60.0652 49.6956 60.75 49.8913C62.1196 50.3804 63.8022 52.4152 59.5761 56.6413" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
);

/* x-mark spark */
const xSpark = (
  <>
    <path d="M25.8262 32.576L30.5218 37.2717" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
    <path d="M30.5217 32.576L25.8261 37.2717" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
  </>
);

/* steam at lower y position (for landing frames 12-14) */
const steamLow1 = (
  <path d="M47.5436 25.2391C46.6632 25.8261 44.9023 27.4696 44.9023 29.3478C44.9023 31.2261 46.2719 32.087 46.9567 32.2826C48.3263 32.7717 50.0089 34.8065 45.7828 39.0326" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
);
const steamLow2 = (
  <path d="M54.2936 25.2391C53.4132 25.8261 51.6523 27.4696 51.6523 29.3478C51.6523 31.2261 53.0219 32.087 53.7067 32.2826C55.0763 32.7717 56.7589 34.8065 52.5328 39.0326" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
);
const steamLow3 = (
  <path d="M61.3371 25.2391C60.4567 25.8261 58.6958 27.4696 58.6958 29.3478C58.6958 31.2261 60.0654 32.087 60.7501 32.2826C62.1197 32.7717 63.8023 34.8065 59.5762 39.0326" stroke="#1B8E56" strokeWidth="2" strokeLinecap="round"/>
);

/* green background behind toast */
const toasterBack = (
  <path d="M20.5435 72.163C20.5435 65.5355 25.916 60.163 32.5435 60.163H74.8695C81.497 60.163 86.8695 65.5355 86.8695 72.163V100.663H20.5435V72.163Z" fill="#7DDDB5" stroke="#1B8E56" strokeWidth="2"/>
);

/* ---------- 14 frames ---------- */
const frames: React.ReactNode[] = [
  /* Frame 1 — toast sitting in slot, lever high */
  <>
    {toasterBack}
    {toastInSlotHigh}
    {toasterBody}
    {leverHigh}
  </>,

  /* Frame 2 — same + 1 steam line */
  <>
    {toasterBack}
    {toastInSlotHigh}
    {toasterBody}
    {steam1}
    {leverHigh}
  </>,

  /* Frame 3 — same + 2 steam lines */
  <>
    {toasterBack}
    {toastInSlotHigh}
    {toasterBody}
    {steam1}
    {steam2}
    {leverHigh}
  </>,

  /* Frame 4 — same + 3 steam lines */
  <>
    {toasterBack}
    {toastInSlotHigh}
    {toasterBody}
    {steam1}
    {steam2}
    {steam3}
    {leverHigh}
  </>,

  /* Frame 5 — toast pops half out, lever low, particles appear */
  <>
    {toasterBack}
    {toastPopHalf}
    {toasterBody}
    {leverLow}
    {particles}
  </>,

  /* Frame 6 — toast tilted -45°, lever low, particles */
  <>
    {toasterBack}
    {toastRotNeg45}
    {toasterBody}
    {leverLow}
    {particles}
  </>,

  /* Frame 7 — toast sideways -90°, lever low, particles */
  <>
    {toasterBack}
    {toastRot90}
    {toasterBody}
    {leverLow}
    {particles}
  </>,

  /* Frame 8 — toast rotated -135°, lever low, particles */
  <>
    {toasterBack}
    {toastRot135}
    {toasterBody}
    {leverLow}
    {particles}
  </>,

  /* Frame 9 — toast at top (upright), lever low, particles */
  <>
    {toasterBack}
    {toastTop}
    {toasterBody}
    {leverLow}
    {particles}
  </>,

  /* Frame 10 — toast mid-air descending, lever low, particles */
  <>
    {toasterBack}
    {toastMidAir}
    {toasterBody}
    {leverLow}
    {particles}
  </>,

  /* Frame 11 — toast popped half, lever low, particles */
  <>
    {toasterBack}
    {toastPopHalf}
    {toasterBody}
    {leverLow}
    {particles}
  </>,

  /* Frame 12 — toast popped half, x-spark + 1 steam, lever low, particles */
  <>
    {toasterBack}
    {toastPopHalf}
    {toasterBody}
    {xSpark}
    {steamLow1}
    {leverLow}
    {particles}
  </>,

  /* Frame 13 — toast popped half, x-spark + 2 steam, lever low, particles */
  <>
    {toasterBack}
    {toastPopHalf}
    {toasterBody}
    {xSpark}
    {steamLow1}
    {steamLow2}
    {leverLow}
    {particles}
  </>,

  /* Frame 14 — toast popped half, x-spark + 3 steam, lever low, particles */
  <>
    {toasterBack}
    {toastPopHalf}
    {toasterBody}
    {xSpark}
    {steamLow1}
    {steamLow2}
    {steamLow3}
    {leverLow}
    {particles}
  </>,
];

export default ToastFlip;
